/**
 * Component that contains all routes that are part of the sign up flow
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Switch } from 'react-router-dom';

import InstancesPageRedirect from '../../MissionCtrl/InstancesPageRedirect/InstancesPageRedirect';
import OrgInfoPageContainer from '../OrgInfoPage/OrgInfoPageContainer';
import VerifyEmailPage from '../VerifyEmailPage/VerifyEmailPage';
import OrgRedirect from '../OrgInfoPage/OrgRedirect/OrgRedirect';
import VerifyEmailRedirect from '../VerifyEmailPage/VerifyEmailRedirect/VerifyEmailRedirect';
import AuthorizedRouteContainer from '../../AuthorizedRoute/AuthorizedRouteContainer';
import ConditionalRoute from './ConditionalRoute/ConditionalRoute';

/**
 * Determine which page should be redirected to based on the user's sign up
 * progress.
 *
 * @param {object} user - The user data to examine
 * @returns {object} - Element representing a <Redirect /> to the proper step
 *   in the sign up flow.
 */
const getRedirect = ({ needsEmailVerification, needsOrg, isAuthenticated }) => {
  if (!isAuthenticated()) {
    return InstancesPageRedirect;
  }
  if (needsEmailVerification) {
    return VerifyEmailRedirect;
  }
  if (needsOrg) {
    return OrgRedirect;
  }
  return InstancesPageRedirect;
};

const propTypes = {
  className: PropTypes.string,
  needsEmailVerification: PropTypes.bool.isRequired,
  needsOrg: PropTypes.bool.isRequired,
  isAuthenticated: PropTypes.func.isRequired,
};

const defaultProps = {
  className: null,
};

function SignUpRoutes({ className, needsEmailVerification, needsOrg, isAuthenticated }) {
  /**
   *  If any of the sign up steps are outstanding, ensure all other routes redirect to
   * the sign up flow.
   */
  const redirect = getRedirect({
    needsEmailVerification,
    needsOrg,
    isAuthenticated,
  });

  return (
    <Switch className={className}>
      <ConditionalRoute
        exact
        conditionFn={() => needsEmailVerification}
        path="/sign-up/verify-email"
        component={VerifyEmailPage}
        fallbackComponent={redirect}
      />
      <ConditionalRoute
        exact
        conditionFn={() => !needsEmailVerification && needsOrg}
        path="/sign-up/org"
        component={OrgInfoPageContainer}
        fallbackComponent={redirect}
      />
      <AuthorizedRouteContainer exact path="/sign-up" component={redirect} />
    </Switch>
  );
}

SignUpRoutes.propTypes = propTypes;
SignUpRoutes.defaultProps = defaultProps;

export default SignUpRoutes;
